import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Home, Building2, Sparkles } from "lucide-react";
import SEOHelmet from "@/components/SEOHelmet";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ContactCTASection from "@/components/ContactCTASection";
import TestimonialsSection from "@/components/TestimonialsSection";
import { useState } from "react";
import EstimateDialog from "@/components/EstimateDialog";

const Cleaning = () => {
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);

  const services = {
    residential: {
      icon: Home,
      title: "Residential Cleaning",
      description: "Keep your Michigan home fresh, organized, and spotless with our comprehensive residential cleaning services.",
      features: [
        "Basic cleaning: weekly, bi-weekly, monthly",
        "Deep cleaning for move-ins & move-outs",
        "Kitchen & bathroom sanitization",
        "Eco-friendly cleaning products available",
        "Floor care & carpet cleaning",
        "Customized cleaning plans for every home"
      ],
      pricing: "Starting at $125/visit | Recurring discounts available"
    },
    commercial: {
      icon: Building2,
      title: "Commercial Cleaning",
      description: "Professional cleaning for Michigan offices, retail spaces, and commercial properties.",
      features: [
        "Office & workspace cleaning",
        "Retail space maintenance",
        "Restroom sanitization",
        "Floor stripping & waxing",
        "Window & glass cleaning",
        "After-hours service available"
      ],
      pricing: "Custom quotes based on space size | Monthly contracts available"
    },
    deep: {
      icon: Sparkles,
      title: "Deep Cleaning Services",
      description: "Intensive, detailed cleaning for Michigan homes that need extra attention and care.",
      features: [
        "Baseboard & trim cleaning",
        "Inside appliances & cabinets",
        "Ceiling fan & light fixture cleaning",
        "Window sill & track detailing",
        "Wall washing & spot treatment",
        "Behind & under furniture"
      ],
      pricing: "Starting at $250 | Perfect for seasonal refresh or move prep"
    }
  };

  return (
    <>
      <SEOHelmet
        title="House Cleaning Services Southwest Michigan | Maid Service Near Me"
        description="Professional house cleaning in Southwest Michigan. Residential, commercial & deep cleaning in St. Joseph, Niles, Buchanan, Benton Harbor. Weekly, bi-weekly & same-day. Free quote."
        keywords="house cleaning Southwest Michigan, cleaning service near me, maid service Berrien County, deep cleaning St Joseph MI, residential cleaning Niles Michigan, commercial cleaning Benton Harbor, house cleaner Buchanan MI, move out cleaning Southwest Michigan, cleaning lady near me, best house cleaning service Michigan"
        canonicalUrl="https://organizebydesignmichigan.com/cleaning"
      />

      <Header onBookingClick={() => setShowEstimateDialog(true)} />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-background via-accent/10 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-serif font-light mb-6">
              Professional Cleaning Services in Michigan
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Keep your Michigan home or business fresh, organized, and spotless—residential and commercial cleaning services designed for lasting results.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button onClick={() => setShowEstimateDialog(true)} size="lg" className="text-lg px-8">
                Get Free Quote
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-8" asChild>
                <a href="tel:+16167270343">Call 616 727-0343</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Tabs */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="residential" className="max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-3 h-auto mb-12">
              <TabsTrigger value="residential" className="py-3">Residential</TabsTrigger>
              <TabsTrigger value="commercial" className="py-3">Commercial</TabsTrigger>
              <TabsTrigger value="deep" className="py-3">Deep Cleaning</TabsTrigger>
            </TabsList>

            {Object.entries(services).map(([key, service]) => {
              const IconComponent = service.icon;
              return (
                <TabsContent key={key} value={key} className="mt-8">
                  <Card className="border-border">
                    <CardHeader className="text-center pb-8">
                      <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                        <IconComponent className="h-8 w-8 text-primary" />
                      </div>
                      <CardTitle className="text-3xl font-serif font-medium mb-3">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-lg">
                        {service.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-8">
                      <div>
                        <h3 className="text-xl font-semibold mb-4">What's Included:</h3>
                        <div className="grid md:grid-cols-2 gap-3">
                          {service.features.map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-accent/20 p-6 rounded-lg">
                        <p className="text-lg font-semibold mb-2">Investment:</p>
                        <p className="text-muted-foreground">{service.pricing}</p>
                      </div>

                      <div className="flex flex-wrap gap-4 justify-center pt-4">
                        <Button onClick={() => setShowEstimateDialog(true)} size="lg" className="px-8">
                          Book This Service
                        </Button>
                        <Button variant="outline" size="lg" className="px-8" asChild>
                          <a href="#contact">Learn More</a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>

      {/* ZenMaid Booking Form */}
      <section className="py-20 bg-accent/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-serif font-light mb-4">
              Book Your Cleaning
            </h2>
            <p className="text-muted-foreground text-lg">
              Schedule your service online — quick, easy, and convenient.
            </p>
          </div>
          <div className="max-w-4xl mx-auto bg-card rounded-xl shadow-lg overflow-hidden">
            <iframe
              sandbox="allow-forms allow-same-origin allow-scripts allow-top-navigation"
              height="4000"
              src="https://app.zenmaid.com/booking-forms/TSF7L/book"
              frameBorder="0"
              scrolling="no"
              style={{ width: '100%' }}
              title="Book cleaning service with Organize by Design"
            />
          </div>
        </div>
      </section>

      <TestimonialsSection />
      <ContactCTASection onBookingClick={() => setShowEstimateDialog(true)} />
      <Footer onBookingClick={() => setShowEstimateDialog(true)} />
      
      <EstimateDialog 
        open={showEstimateDialog} 
        onOpenChange={setShowEstimateDialog}
      />
    </>
  );
};

export default Cleaning;
